import { t } from "ttag";
import _ from "underscore";

import {
  ChartSettingsError,
  MinRowsError,
} from "metabase/visualizations/lib/errors";
import { formatValue } from "metabase/visualizations/lib/formatting";
import { columnSettings } from "metabase/visualizations/lib/settings/column";
import { nestedSettings } from "metabase/visualizations/lib/settings/nested";
import { keyForSingleSeries } from "metabase/visualizations/lib/settings/series";
import {
  dimensionSetting,
  metricSetting,
} from "metabase/visualizations/lib/settings/utils";
import {
  getDefaultPercentVisibility,
  getDefaultPieColumns,
  getDefaultShowLabels,
  getDefaultShowLegend,
  getDefaultShowTotal,
  getDefaultSliceThreshold,
  getDefaultSortRows,
  getPieRows,
  getPieSortRowsDimensionSetting,
} from "metabase/visualizations/shared/settings/pie";
import { SERIES_SETTING_KEY } from "metabase/visualizations/shared/settings/series";
import {
  getDefaultSize,
  getMinSize,
} from "metabase/visualizations/shared/utils/sizes";
import type { VisualizationDefinition } from "metabase/visualizations/types";
import { isDimension, isMetric } from "metabase-lib/v1/types/utils/isa";
import type { SingleSeries } from "metabase-types/api";

import type { SliceNameWidgetProps } from "./SliceNameWidget";

const pieRowsReadDeps = [
  "pie.dimension",
  "pie.metric",
  "pie.colors",
  "pie.sort_rows",
  "pie.slice_threshold",
];

export const PIE_CHART_DEFINITION: VisualizationDefinition = {
  getUiName: () => t`Pie`,
  identifier: "pie",
  iconName: "pie",
  usesEChartsRenderer: true,
  minSize: getMinSize("pie"),
  defaultSize: getDefaultSize("pie"),
  supportsVisualizer: true,
  isSensible: ({ cols, rows }) => {
    const numDimensions = cols.filter(isDimension).length;
    const numMetrics = cols.filter(isMetric).length;

    return (
      rows.length >= 2 &&
      cols.length >= 2 &&
      numDimensions >= 1 &&
      numMetrics >= 1
    );
  },
  checkRenderable: (
    [
      {
        data: { rows },
      },
    ],
    settings,
  ) => {
    // This prevents showing "Which columns do you want to use" when
    // the piechart is displayed with no results in the dashboard
    if (rows.length < 1) {
      throw new MinRowsError(0);
    }
    const isDimensionMissing =
      !settings["pie.dimension"] ||
      (Array.isArray(settings["pie.dimension"]) &&
        settings["pie.dimension"].every((col) => col == null));
    if (isDimensionMissing || !settings["pie.metric"]) {
      throw new ChartSettingsError(t`Which columns do you want to use?`, {
        section: t`Data`,
      });
    }
  },
  hasEmptyState: true,
  settings: {
    ...metricSetting("pie.metric", {
      getSection: () => t`Data`,
      get title() {
        return t`Measure`;
      },
      showColumnSetting: true,
      getDefault: (rawSeries) => getDefaultPieColumns(rawSeries).metric,
    }),
    ...columnSettings({ getHidden: () => true }),
    ...dimensionSetting("pie.dimension", {
      getHidden: () => true,
      get title() {
        return t`Dimension`;
      },
      showColumnSetting: true,
      getDefault: (rawSeries) => getDefaultPieColumns(rawSeries).dimension,
    }),
    "pie.rows": {
      getHidden: () => true,
      getValue: _.memoize(
        (series, settings) => {
          return getPieRows(series, settings, (value, options) =>
            String(formatValue(value, options)),
          );
        },
        ([{ data }], settings) =>
          JSON.stringify({
            cols: data.cols,
            rows: data.rows,
            settings: _.pick(
              settings,
              ...pieRowsReadDeps,
              "pie.rows",
              "pie.sort_rows_dimension",
            ),
          }),
      ),
      readDependencies: pieRowsReadDeps,
      writeDependencies: ["pie.sort_rows_dimension"],
    },
    "pie.sort_rows_dimension": {
      getValue: (_series, settings) => getPieSortRowsDimensionSetting(settings),
      // This read dependency is set so that "pie.sort_rows" is computed *before* this value, ensuring that
      // that it uses the stored value if one exists. This is needed to check if the dimension has actually changed
      readDependencies: ["pie.sort_rows", "pie.dimension"],
    },
    "pie.sort_rows": {
      getHidden: () => true,
      getDefault: getDefaultSortRows,
    },
    ...nestedSettings<
      "series_settings",
      SingleSeries,
      unknown,
      SliceNameWidgetProps
    >(SERIES_SETTING_KEY, {
      getObjectKey: keyForSingleSeries,
      widget: "pieSliceName",
      getHidden: ([{ card }], _settings, extra) =>
        !extra?.isDashboard || card?.display === "waterfall",
      getSection: (_series, _settings, extra) =>
        extra?.isDashboard ? t`Display` : t`Style`,
      getWrapperStyle: () => ({
        marginBottom: 0,
      }),
      getProps: (_series, vizSettings, _onChange, _extra, onChangeSettings) => {
        const pieRows = vizSettings["pie.rows"];
        if (pieRows == null) {
          return { pieRows: [], updateRowName: () => null };
        }

        return {
          pieRows,
          updateRowName: (newName: string, key: string | number) => {
            onChangeSettings({
              "pie.rows": pieRows.map((row) => {
                if (row.key !== key) {
                  return row;
                }
                return { ...row, name: newName };
              }),
            });
          },
        };
      },
      readDependencies: ["pie.rows"],
    }),

    "pie._dimensions_widget": {
      getSection: () => t`Data`,
      widget: "pieDimensions",
      getProps: (rawSeries, settings, _onChange, _extra, onChangeSettings) => ({
        rawSeries,
        settings,
        onChangeSettings,
      }),
      readDependencies: ["pie.dimension", "pie.rows"],
    },
    "pie.show_legend": {
      getSection: () => t`Display`,
      get title() {
        return t`Show legend`;
      },
      widget: "toggle",
      getDefault: getDefaultShowLegend,
      inline: true,
      getWrapperStyle: () => ({
        marginBottom: "1rem",
      }),
    },
    "pie.show_total": {
      getSection: () => t`Display`,
      get title() {
        return t`Show total`;
      },
      widget: "toggle",
      getDefault: getDefaultShowTotal,
      inline: true,
      getWrapperStyle: () => ({
        marginBottom: "1rem",
      }),
    },
    "pie.show_labels": {
      getSection: () => t`Display`,
      get title() {
        return t`Show labels`;
      },
      widget: "toggle",
      getDefault: (_rawSeries, settings) => getDefaultShowLabels(settings),
      inline: true,
    },
    "pie.percent_visibility": {
      getSection: () => t`Display`,
      get title() {
        return t`Show percentages`;
      },
      widget: "radio",
      getDefault: getDefaultPercentVisibility,
      getProps: () => ({
        options: [
          {
            get name() {
              return t`Off`;
            },
            value: "off",
          },
          {
            get name() {
              return t`In legend`;
            },
            value: "legend",
          },
          {
            get name() {
              return t`On the chart`;
            },
            value: "inside",
          },
          {
            get name() {
              return t`Both`;
            },
            value: "both",
          },
        ],
      }),
    },
    "pie.decimal_places": {
      getSection: () => t`Display`,
      get title() {
        return t`Number of decimal places`;
      },
      widget: "number",
      getProps: () => ({
        get placeholder() {
          return t`Auto`;
        },
        options: { isInteger: true, isNonNegative: true },
      }),
      getHidden: (_, settings) =>
        settings["pie.percent_visibility"] == null ||
        settings["pie.percent_visibility"] === "off",
      readDependencies: ["pie.percent_visibility"],
    },
    "pie.slice_threshold": {
      getSection: () => t`Display`,
      get title() {
        return t`Minimum slice percentage`;
      },
      widget: "number",
      getDefault: getDefaultSliceThreshold,
    },
    "tooltip.show_percent": {
      getSection: () => t`Display`,
      get title() {
        return t`Show percentage in tooltip`;
      },
      widget: "toggle",
      inline: true,
      getDefault: () => true,
    },
    "tooltip.show_total": {
      getSection: () => t`Display`,
      get title() {
        return t`Show total in tooltip`;
      },
      widget: "toggle",
      inline: true,
      getDefault: () => true,
    },
  },
};
